/**
 * iPhone (and many other) cameras store photos using the sensor's native
 * landscape pixels plus an EXIF orientation tag describing how they should be
 * rotated for display. Some environments — notably the iOS WKWebView the app
 * runs in — don't honor that tag, so portrait photos render rotated 90° CCW.
 *
 * This re-encodes the image through a canvas with the EXIF orientation applied,
 * baking the correct rotation into the pixels and stripping the now-redundant
 * EXIF tag. The resulting Blob looks correct in every viewer, including the
 * preview and any downstream consumer of the uploaded bytes.
 *
 * Camera photos are also huge (e.g. 4032×3024 ≈ 12MP). Re-encoding at full
 * resolution blocks the main thread and makes the preview appear only after a
 * noticeable delay, so we cap the longest edge to maxDimension. The downscale
 * is applied as drawImage destination dimensions, which is universally
 * supported, including in the iOS WKWebView.
 *
 * If anything goes wrong (unsupported browser, decode failure, …) the original
 * file is returned unchanged so the upload still works.
 */
const maxDimension = 1600

const getScaledSize = (width: number, height: number): { width: number; height: number } => {
  const largestEdge = Math.max(width, height)

  if (largestEdge <= maxDimension) {
    return { width, height }
  }

  const scale = maxDimension / largestEdge

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  }
}

export const normalizeImageOrientation = async (file: Blob): Promise<Blob> => {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return file
  }

  let bitmap: ImageBitmap | undefined

  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })

    const { width, height } = getScaledSize(bitmap.width, bitmap.height)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')

    if (!context) {
      return file
    }

    context.drawImage(bitmap, 0, 0, width, height)

    // Always encode to JPEG. canvas.toBlob can only produce a handful of formats
    // (image/png and, where supported, image/jpeg / image/webp). Echoing back the
    // source type — e.g. the image/heic an iPhone often delivers — makes toBlob
    // return null, after which we'd fall back to the undisplayable original and
    // the preview stays blank.
    const normalized = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92)
    })

    return normalized && normalized.size > 0 ? normalized : file
  } catch {
    return file
  } finally {
    bitmap?.close()
  }
}
